import { useMutation } from "@apollo/client/react"
import { FileChecksum } from "@rails/activestorage/src/file_checksum"
import { Download, Mic, Square, Upload } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { gql } from "~/__generated__"
import { RecordingSectionFragment } from "~/__generated__/graphql"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { TRANSCRIBE_RECORDING, UPLOAD_RECORDING } from "./editorial-queries"
import { forgetRecording, recoverRecordings, rememberRecording } from "./recording-recovery"
import { TranscriptEditor } from "./transcript-editor"

const INCLUDE_RECORDING = gql(/* GraphQL */ `
  mutation IncludeRecording($input: RecordingIncludeInput!) {
    recordingInclude(input: $input) {
      section {
        ...RecordingSection
      }
    }
  }
`)

type LocalSection = {
  key: string
  postId: string
  blob: Blob
  url: string
  name: string
  error?: string
  order?: number
}
const checksum = (file: File) =>
  new Promise<string>((resolve, reject) =>
    FileChecksum.create(file, (error, value) =>
      error || !value ? reject(new Error(error ?? "Could not read recording")) : resolve(value)
    )
  )

export function ReactionRecorder({
  postId,
  onPendingChange,
  ensurePost,
  onSaved,
  sections,
  editable,
}: {
  postId?: string
  onPendingChange: (pending: boolean) => void
  ensurePost: () => Promise<string>
  onSaved: () => Promise<void>
  sections: RecordingSectionFragment[]
  editable: boolean
}) {
  const [dirtyTranscripts, setDirtyTranscripts] = useState<string[]>([])
  const onTranscriptDirty = useCallback((id: string, dirty: boolean) => {
    setDirtyTranscripts((items) =>
      dirty
        ? items.includes(id)
          ? items
          : [...items, id]
        : items.includes(id)
          ? items.filter((item) => item !== id)
          : items
    )
  }, [])
  const [recording, setRecording] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [pending, setPending] = useState<LocalSection[]>([])
  const [error, setError] = useState<Error | null>(null)
  const [includeRecording] = useMutation(INCLUDE_RECORDING)
  const fileInput = useRef<HTMLInputElement>(null)
  const captureOrder = useRef(0)
  const recoveryLoaded = useRef(new Set<string>())
  const [upload] = useMutation(UPLOAD_RECORDING)
  const [transcribe] = useMutation(TRANSCRIBE_RECORDING)
  const video = useRef<HTMLVideoElement>(null)
  const stream = useRef<MediaStream | null>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  const alive = useRef(true)
  const urls = useRef(new Set<string>())
  const uploadQueue = useRef(Promise.resolve())
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!postId || recoveryLoaded.current.has(postId)) return
    recoveryLoaded.current.add(postId)
    void recoverRecordings(postId)
      .then((records) => {
        if (!alive.current) return
        setPending((items) => [
          ...items,
          ...records
            .filter((record) => !items.some((item) => item.key === record.key))
            .map((record) => {
              const url = URL.createObjectURL(record.blob)
              urls.current.add(url)
              return {
                ...record,
                url,
                error: "Recovered from this browser. Retry saving to finish the upload.",
              }
            }),
        ])
      })
      .catch(() => {})
  }, [postId])

  useEffect(() => {
    onPendingChange(recording || pending.length > 0 || requesting || dirtyTranscripts.length > 0)
    return () => onPendingChange(false)
  }, [recording, pending.length, requesting, dirtyTranscripts.length, onPendingChange])
  useEffect(() => {
    alive.current = true
    const activeUrls = urls.current
    return () => {
      alive.current = false
      if (stopTimer.current) clearTimeout(stopTimer.current)
      if (recorder.current?.state === "recording") recorder.current.stop()
      stream.current?.getTracks().forEach((track) => track.stop())
      activeUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [])
  useEffect(() => {
    if (!recording && !pending.length && !requesting && !dirtyTranscripts.length) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [recording, pending.length, requesting, dirtyTranscripts.length])

  const save = async (section: LocalSection) => {
    if (!alive.current) return
    setPending((items) =>
      items.map((item) => (item.key === section.key ? { ...item, error: undefined } : item))
    )
    try {
      if (!section.blob.size)
        throw new Error(
          "No video frames were captured. Try recording for a few seconds before stopping."
        )
      if (section.blob.size > 250 * 1024 * 1024)
        throw new Error(
          "Each section must be under 250 MB. Download this recording and split it into shorter sections."
        )
      const file = new File([section.blob], section.name, { type: section.blob.type.split(";")[0] })
      const result = await upload({
        variables: {
          input: {
            postId: section.postId,
            clientKey: section.key,
            byteSize: file.size,
            checksum: await checksum(file),
            contentType: file.type,
          },
        },
      })
      if (result.error) throw result.error
      const prepared = result.data?.recordingUpload
      if (!prepared) throw new Error("Could not prepare the upload")
      if (prepared.uploadUrl) {
        const response = await fetch(prepared.uploadUrl, {
          method: "PUT",
          headers: prepared.uploadHeaders as Record<string, string>,
          body: file,
          signal: AbortSignal.timeout(180000),
        })
        if (!response.ok)
          throw new Error(
            "Upload failed. Your recording is still available below; retry saving it."
          )
      }
      const queued = await transcribe({ variables: { input: { id: prepared.section.id } } })
      if (queued.error) throw queued.error
      await onSaved()
      await forgetRecording(section.key).catch(() => {})
      if (alive.current) setPending((items) => items.filter((item) => item.key !== section.key))
      URL.revokeObjectURL(section.url)
      urls.current.delete(section.url)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not save the section"
      if (alive.current)
        setPending((items) =>
          items.map((item) => (item.key === section.key ? { ...item, error: message } : item))
        )
    }
  }
  const queueSave = (section: LocalSection) => {
    section.order ??= Date.now() * 1000 + (captureOrder.current++ % 1000)
    const remembered = rememberRecording(section).catch(() => {
      setError(
        new Error(
          "Browser recovery storage is unavailable. Keep this page open until saving finishes, or download the recording."
        )
      )
    })
    uploadQueue.current = uploadQueue.current.then(async () => {
      await remembered
      await save(section)
    })
  }

  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length) return
    setError(null)
    setRequesting(true)
    try {
      const targetId = await ensurePost()
      for (const file of Array.from(files)) {
        if (!/\.(mp4|mov|webm)$/i.test(file.name) || file.size > 250 * 1024 * 1024)
          throw new Error("Choose MP4, MOV, or WebM files under 250 MB each.")
        const type = /\.webm$/i.test(file.name)
          ? "video/webm"
          : /\.mov$/i.test(file.name)
            ? "video/quicktime"
            : "video/mp4"
        const blob = file.slice(0, file.size, type)
        const url = URL.createObjectURL(blob)
        urls.current.add(url)
        const section = { key: crypto.randomUUID(), postId: targetId, blob, url, name: file.name }
        setPending((items) => [...items, section])
        queueSave(section)
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Could not upload the video"))
    } finally {
      setRequesting(false)
      if (fileInput.current) fileInput.current.value = ""
    }
  }

  const start = async () => {
    setError(null)
    setRequesting(true)
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined")
        throw new Error("Recording requires Chrome on localhost or HTTPS.")
      const postId = await ensurePost()
      const media = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      })
      if (!alive.current) {
        media.getTracks().forEach((track) => track.stop())
        return
      }
      stream.current = media
      const mimeType = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/mp4",
      ].find((type) => MediaRecorder.isTypeSupported(type))
      const capture = new MediaRecorder(media, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: 1500000,
      })
      recorder.current = capture
      const chunks: BlobPart[] = []
      capture.ondataavailable = (event) => {
        if (event.data.size) chunks.push(event.data)
      }
      capture.onstop = () => {
        if (stopTimer.current) clearTimeout(stopTimer.current)
        media.getTracks().forEach((track) => track.stop())
        if (!alive.current) return
        if (video.current) video.current.srcObject = null
        const type = capture.mimeType || "video/webm"
        const blob = new Blob(chunks, { type })
        const url = URL.createObjectURL(blob)
        urls.current.add(url)
        const section = {
          key: crypto.randomUUID(),
          postId,
          blob,
          url,
          name: type.includes("mp4") ? "reaction-section.mp4" : "reaction-section.webm",
        }
        setPending((items) => [...items, section])
        setRecording(false)
        queueSave(section)
      }
      capture.onerror = () => {
        setError(
          new Error("Recording was interrupted. Any recovered video will be saved as a section.")
        )
        if (capture.state !== "inactive") capture.stop()
      }
      if (video.current) {
        video.current.srcObject = media
        void video.current.play().catch(() => {})
      }
      capture.start(1000)
      setRecording(true)
      stopTimer.current = setTimeout(() => {
        if (capture.state === "recording") capture.stop()
      }, 590000)
    } catch (err) {
      stream.current?.getTracks().forEach((track) => track.stop())
      if (alive.current)
        setError(
          err instanceof Error ? err : new Error("Could not access the camera and microphone.")
        )
    } finally {
      if (alive.current) setRequesting(false)
    }
  }

  return (
    <section
      id="reaction-recording"
      aria-label="Record your reaction"
      className="scroll-mt-6 rounded-2xl border bg-card p-6"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        03 · Bring your voice
      </p>
      <h3 className="mt-3 text-xl font-semibold">Build your reaction in sections</h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        Record your opening, examples, and follow-up thoughts separately. Each section is saved and
        transcribed as soon as you stop. Keep going while earlier sections process. Sections stop
        automatically just before 10 minutes. You can also upload existing videos. Exclude a section
        to leave it out of future posts and clips while keeping the original.
      </p>
      <video
        ref={video}
        muted
        autoPlay
        playsInline
        hidden={!recording}
        className="mt-5 aspect-video max-h-96 w-full rounded-xl bg-black"
        aria-label="Live camera preview"
      />
      {recording && (
        <p role="status" className="mt-4 font-medium text-destructive">
          Recording camera and microphone…
        </p>
      )}
      {error && (
        <div className="mt-4">
          <GraphqlError error={error} />
        </div>
      )}
      {editable && (
        <div className="mt-5 flex flex-wrap gap-3">
          <input
            ref={fileInput}
            type="file"
            accept=".mp4,.mov,.webm"
            multiple
            hidden
            onChange={(event) => void uploadFiles(event.target.files)}
          />
          {!recording && (
            <Button
              variant="outline"
              disabled={requesting}
              onClick={() => fileInput.current?.click()}
            >
              <Upload className="size-4" />
              Upload video sections
            </Button>
          )}
          {recording ? (
            <Button
              onClick={() => {
                if (recorder.current?.state === "recording") recorder.current.stop()
              }}
            >
              <Square className="size-4" /> Stop and save section
            </Button>
          ) : (
            <Button onClick={start} disabled={requesting}>
              <Mic className="size-4" />
              {requesting
                ? "Preparing camera…"
                : sections.length || pending.length
                  ? "Record additional section"
                  : "Start camera recording"}
            </Button>
          )}
        </div>
      )}
      {editable && sections.length > 0 && (
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            className="mt-3"
            onClick={() =>
              document
                .getElementById("post-writing")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            Go to post writer
          </Button>
          <Button
            variant="outline"
            className="mt-3"
            onClick={() =>
              document
                .getElementById("social-clips")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            Go to clips & cleanup
          </Button>
        </div>
      )}
      <div className="mt-6 space-y-5">
        {sections.map((section) => (
          <article
            key={section.id}
            className="rounded-xl border p-4"
            aria-label={`Section ${section.position}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h4 className="font-semibold">
                Section {section.position}
                {!section.included && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground">Excluded</span>
                )}
              </h4>
              {editable && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const result = await includeRecording({
                        variables: { input: { id: section.id, included: !section.included } },
                      })
                      if (result.error) throw result.error
                      await onSaved()
                    } catch (err) {
                      setError(err instanceof Error ? err : new Error("Could not update section"))
                    }
                  }}
                >
                  {section.included ? "Exclude section" : "Include section"}
                </Button>
              )}
              <span className="text-sm text-muted-foreground">
                {section.status === "ready"
                  ? "Transcript ready"
                  : section.status === "failed"
                    ? "Needs attention"
                    : section.status === "uploading"
                      ? "Awaiting upload"
                      : "Transcribing…"}
              </span>
            </div>
            {section.videoUrl && (
              <>
                <video
                  src={section.videoUrl}
                  controls
                  preload="metadata"
                  playsInline
                  className="mt-4 aspect-video max-h-72 w-full rounded-lg bg-black"
                  aria-label={`Section ${section.position} preview`}
                />
                <a
                  href={`${section.videoUrl}?download=1`}
                  className="mt-3 inline-flex items-center gap-2 text-sm underline"
                >
                  <Download className="size-4" /> Download section {section.position}
                </a>
              </>
            )}
            {section.transcript && (
              <TranscriptEditor
                section={section}
                editable={editable}
                onDirtyChange={onTranscriptDirty}
                onSaved={onSaved}
              />
            )}
            {section.error && (
              <p role="alert" className="mt-3 text-sm text-destructive">
                {section.error}
              </p>
            )}
            {editable && (section.status === "failed" || section.status === "queued") && (
              <Button
                variant="outline"
                className="mt-3"
                onClick={async () => {
                  try {
                    const result = await transcribe({ variables: { input: { id: section.id } } })
                    if (result.error) throw result.error
                    await onSaved()
                  } catch (err) {
                    setError(
                      err instanceof Error ? err : new Error("Could not retry transcription")
                    )
                  }
                }}
              >
                Retry transcription
              </Button>
            )}
          </article>
        ))}
        {pending.map((section) => (
          <article key={section.key} className="rounded-xl border p-4">
            <h4 className="font-semibold">
              {section.error ? "Section needs saving" : "Saving your next section…"}
            </h4>
            <video
              src={section.url}
              controls
              className="mt-3 aspect-video max-h-72 w-full rounded-lg bg-black"
              aria-label="Unsaved section preview"
            />
            <a
              href={section.url}
              download={section.name}
              className="mt-3 inline-block text-sm underline"
            >
              Download recording
            </a>
            {section.error && (
              <>
                <p className="mt-2 text-sm text-destructive" role="alert">
                  {section.error}
                </p>
                <Button variant="outline" className="mt-3" onClick={() => queueSave(section)}>
                  Retry saving section
                </Button>
              </>
            )}
          </article>
        ))}
      </div>
    </section>
  )
}
